import { type RefObject, useCallback, useEffect, useRef } from 'react'

const DEFAULT_IDLE_MS = 3000
const HOVER_POLL_MS = 120

function isShellEngaged(el: HTMLElement): boolean {
  return el.matches(':hover') || el.contains(document.activeElement)
}

/**
 * 展开态空闲收起：指针在壳层内或焦点在壳层内时不计时；离开后约 {@link idleMs} 再收起。
 * 使用轮询 `:hover` + focus，避免子元素间移动误触发 pointerleave。
 */
export function useDynamicIslandIdleCollapse(
  shellRef: RefObject<HTMLDivElement | null>,
  expanded: boolean,
  setExpanded: (next: boolean) => void,
  idleMs = DEFAULT_IDLE_MS,
) {
  const engagedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleCollapse = useCallback(() => {
    clearTimer()
    if (!expanded || engagedRef.current) return
    timerRef.current = window.setTimeout(() => {
      const el = shellRef.current
      if (el && isShellEngaged(el)) {
        engagedRef.current = true
        return
      }
      if (!engagedRef.current) setExpanded(false)
    }, idleMs)
  }, [clearTimer, expanded, idleMs, setExpanded, shellRef])

  const applyEngaged = useCallback(
    (next: boolean) => {
      if (engagedRef.current === next) return
      engagedRef.current = next
      if (next) clearTimer()
      else scheduleCollapse()
    },
    [clearTimer, scheduleCollapse],
  )

  useEffect(() => {
    if (!expanded) {
      engagedRef.current = false
      clearTimer()
      return
    }

    const sync = () => {
      const el = shellRef.current
      if (!el) return
      applyEngaged(isShellEngaged(el))
    }

    sync()
    const pollId = window.setInterval(sync, HOVER_POLL_MS)

    const el = shellRef.current
    const onFocusIn = () => sync()
    const onFocusOut = () => window.setTimeout(sync, 0)
    const onPointerDown = () => applyEngaged(true)
    const onScroll = () => applyEngaged(true)

    el?.addEventListener('focusin', onFocusIn)
    el?.addEventListener('focusout', onFocusOut)
    el?.addEventListener('pointerdown', onPointerDown, true)
    el?.addEventListener('scroll', onScroll, true)

    return () => {
      window.clearInterval(pollId)
      clearTimer()
      el?.removeEventListener('focusin', onFocusIn)
      el?.removeEventListener('focusout', onFocusOut)
      el?.removeEventListener('pointerdown', onPointerDown, true)
      el?.removeEventListener('scroll', onScroll, true)
    }
  }, [expanded, shellRef, clearTimer, applyEngaged])
}
